import { ErrorStateMatcher } from '@angular/material/core';
import { UntypedFormControl, FormGroupDirective, NgForm, UntypedFormGroup } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';

export class AppErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }

  /**
   * Attach serverError to FormControl.errors, so errors can be rendered in form
   * @param form FormGroup validated form
   * @param resp HttpErrorResponse server error response
   * @param KeyMap simple lookup object where server-keys doesn't match with formcontrol names
   *               E.g. { username: 'email' }, username is server-key, email is form control name
   */
  setServerErrors(form: UntypedFormGroup, resp: HttpErrorResponse, keyMap: object = {}): void {
    const respBody = resp.error;

    // Handle different response structures:
    // 1. resp.error = { error: { phone: {...} } } (nested error)
    // 2. resp.error = { phone: {...} } (direct error object)
    // 3. resp.error = { status: 422, error: { phone: {...} } } (full response body)
    let errorObj = respBody;

    if (respBody?.error && typeof respBody.error === 'object') {
      errorObj = respBody.error;
    } else if (respBody && typeof respBody === 'object' && !Array.isArray(respBody)) {
      // Check if respBody has status field (full response body)
      if ('status' in respBody && respBody.error) {
        errorObj = respBody.error;
      } else {
        errorObj = respBody;
      }
    }

    if (!errorObj || typeof errorObj !== 'object' || Array.isArray(errorObj)) {
      return;
    }

    for (let [prop, error] of Object.entries(errorObj)) {
      // Skip non-error fields like 'status'
      if (prop === 'status' || prop === 'message') {
        continue;
      }

      // assign custom field errors to form controls
      if (/^field_values\.(.*)/.test(prop)) {
        prop = prop.match(/^field_values\.(.*)/)[1];
      }

      const formControl = form.get(prop in keyMap ? keyMap[prop] : prop);
      if (formControl) {
        let errorMessage: string;

        if (error && typeof error === 'object' && !Array.isArray(error)) {
          // Error object with msg property
          if (error['msg']) {
            errorMessage = String(error['msg']);
          } else if (error['message']) {
            errorMessage = String(error['message']);
          } else {
            // Fallback: try to stringify, but prefer first string value
            const firstStringValue = Object.values(error).find(v => typeof v === 'string');
            errorMessage = firstStringValue ? String(firstStringValue) : JSON.stringify(error);
          }
        } else if (typeof error === 'string') {
          errorMessage = error;
        } else {
          errorMessage = String(error);
        }

        formControl.setErrors({ error: errorMessage });
        formControl.markAsDirty();
      }
    }
  }
}
